import { useState } from 'react';
import { Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';
import { tracking } from '../../util/tracking';

const ChangeEmailModal: React.FC<{ show: boolean; onHide: () => void }> = ({
  show,
  onHide,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setNewEmail('');
    setCurrentPassword('');
    setErrorMsg(null);
    setSuccessMsg(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleSave = async () => {
    tracking.log('UI', 'settings_change_email', { page: 'settings' });
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!newEmail || !currentPassword) {
      setErrorMsg('All fields are required');
      return;
    }

    setLoading(true);

    try {
      // get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('Not authenticated');
      }

      // 🔐 Re-authenticate to verify identity
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: currentPassword,
      });

      if (reauthError) {
        throw new Error('Current password is incorrect');
      }

      // 📧 Request email change
      const { error: updateError } = await supabase.auth.updateUser(
        {
          email: newEmail,
        },
        {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        }
      );

      if (updateError) throw updateError;

      setSuccessMsg(
        'Confirmation email sent. Please check your new email address to complete the change.'
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'auth.updateEmail' });
      setErrorMsg(err.message || 'Failed to change email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change Email</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>New Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="example@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Current Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="••••••••"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Form.Group>

          <Alert variant="info">
            You will receive a confirmation email at the new address before the
            change takes effect.
          </Alert>

          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChangeEmailModal;
